/**
 * 에이전트별 샘플 데이터 섹션
 *
 * Interview Coach: 샘플 이력서, 채용 공고 데이터
 * YouTube Insight: 샘플 유튜브 URL 목록
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { INTERVIEW_COACH_SAMPLE_DATA, YOUTUBE_INSIGHT_SAMPLE_DATA } from './sample-data';

/** Interview Coach 에이전트 slug */
const INTERVIEW_COACH_AGENT_SLUG = 'interview-strategy-coach-v1';
/** YouTube Insight 에이전트 slug */
const YOUTUBE_INSIGHT_AGENT_SLUG = 'youtube';

interface SampleDataSectionProps {
  /** 에이전트 slug */
  agentSlug?: string;
  /** 필드 값 변경 콜백 */
  onFieldChange?: (fieldId: string, value: string) => void;
}

/**
 * 샘플 데이터 카드 (Interview Coach용 - 긴 텍스트)
 */
interface SampleCardProps {
  title: string;
  text: string;
  onApply?: (value: string) => void;
}

function SampleCard({ title, text, onApply }: SampleCardProps) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${title} 복사됨`);
    } catch {
      toast.error('복사에 실패했습니다');
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply(text);
      toast.success(`${title} 적용됨`);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{title}</span>
        <div className="flex gap-1">
          {onApply && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleApply}
            >
              적용
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleCopy}
          >
            복사
          </Button>
        </div>
      </div>
      <pre className="max-h-24 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
        {text.slice(0, 150)}...
      </pre>
    </div>
  );
}

/**
 * URL 샘플 카드 (YouTube Insight용 - 짧은 URL)
 */
interface UrlSampleCardProps {
  url: string;
  index: number;
  onApply?: (value: string) => void;
}

function UrlSampleCard({ url, index, onApply }: UrlSampleCardProps) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL 복사됨');
    } catch {
      toast.error('복사에 실패했습니다');
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply(url);
      toast.success('URL 적용됨');
    }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-2">
      <span className="text-xs text-muted-foreground truncate flex-1">
        샘플 {index + 1}: {url}
      </span>
      <div className="flex gap-1 shrink-0 ml-2">
        {onApply && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={handleApply}
          >
            적용
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={handleCopy}
        >
          복사
        </Button>
      </div>
    </div>
  );
}

/**
 * Interview Coach 샘플 데이터 섹션
 */
function InterviewCoachSampleSection({
  onFieldChange,
}: {
  onFieldChange?: (fieldId: string, value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <SampleCard
        title="샘플 이력서"
        text={INTERVIEW_COACH_SAMPLE_DATA.resumeText}
        onApply={onFieldChange ? (v) => onFieldChange('resumeText', v) : undefined}
      />
      <SampleCard
        title="샘플 채용 공고"
        text={INTERVIEW_COACH_SAMPLE_DATA.jobDescriptionText}
        onApply={onFieldChange ? (v) => onFieldChange('jobDescriptionText', v) : undefined}
      />
    </div>
  );
}

/**
 * YouTube Insight 샘플 데이터 섹션
 */
function YouTubeInsightSampleSection({
  onFieldChange,
}: {
  onFieldChange?: (fieldId: string, value: string) => void;
}) {
  return (
    <div className="space-y-2">
      {YOUTUBE_INSIGHT_SAMPLE_DATA.urls.map((url, index) => (
        <UrlSampleCard
          key={url}
          url={url}
          index={index}
          onApply={onFieldChange ? (v) => onFieldChange('url', v) : undefined}
        />
      ))}
    </div>
  );
}

/**
 * 샘플 데이터 섹션 (에이전트별 분기)
 */
export function SampleDataSection({ agentSlug, onFieldChange }: SampleDataSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  // 지원하는 에이전트인지 확인
  const isSupported =
    agentSlug === INTERVIEW_COACH_AGENT_SLUG || agentSlug === YOUTUBE_INSIGHT_AGENT_SLUG;

  if (!isSupported) return null;

  return (
    <div className="mb-4">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full justify-between text-xs"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>📋 샘플 데이터</span>
        <span className="text-muted-foreground">{isOpen ? '접기' : '펼치기'}</span>
      </Button>
      {isOpen && (
        <div className="mt-2">
          {agentSlug === INTERVIEW_COACH_AGENT_SLUG && (
            <InterviewCoachSampleSection onFieldChange={onFieldChange} />
          )}
          {agentSlug === YOUTUBE_INSIGHT_AGENT_SLUG && (
            <YouTubeInsightSampleSection onFieldChange={onFieldChange} />
          )}
        </div>
      )}
    </div>
  );
}
